import React, { FunctionComponent, useRef, useState } from 'react';
import { Tooltip as ReactTooltip, ITooltip } from 'react-tooltip';
import styled, { useTheme } from 'styled-components';
import Icon, { IIconProps } from '../Icon';
import { Property } from 'csstype';
import { uniqueId } from 'lodash';

type LegacyEvent = 'click' | 'dblclick' | 'mousedown' | 'mouseup' | 'mouseenter' | 'focus' | 'mouseover';

interface Props extends ITooltip {
  delay?: number;
  text: string;
  content?: React.ReactNode;
  event?: LegacyEvent;
  config?: Config;
  iconProps?: IIconProps;
  style?: React.CSSProperties;
  tooltipBackgroundColor?: string;
}

interface Config {
  maxWidth?: Property.MaxWidth;
  verticalAlign?: Property.VerticalAlign;
}

const StyledTooltip = styled.span<Config>`
  i {
    vertical-align: ${(props) => props.verticalAlign || 'bottom'};
  }

  i:hover {
    cursor: pointer;
  }
`;

export const Tooltip: FunctionComponent<React.PropsWithChildren<Props>> = (props) => {
  const theme = useTheme();
  const [active, setActive] = useState(false);
  const id = useRef(uniqueId('tooltip_')).current;
  const config = props.config ?? {};
  const portalRoot = typeof document !== 'undefined' ? document.body : null;

  const {
    children,
    content,
    delay,
    event,
    iconProps,
    config: _config,
    style,
    text,
    tooltipBackgroundColor,
    openOnClick,
    openEvents,
    closeEvents,
    afterShow,
    afterHide,
    ...tooltipProps
  } = props;

  // Destructured above only to exclude from tooltipProps spread
  void _config;

  const legacyOpenEvents = event ? { [event]: true } : undefined;
  const legacyOpenOnClick = event === 'click' || event === 'dblclick' ? true : undefined;
  const resolvedOpenOnClick = legacyOpenOnClick ?? openOnClick;
  const resolvedOpenEvents = legacyOpenEvents ?? openEvents;

  return (
    <StyledTooltip {...config}>
      <span
        style={style}
        data-tooltip-id={id}
        data-tooltip-content={text}
      >
        {children ? (
          children // Render custom content if provided
        ) : (
          <Icon
            {...iconProps}
            className={iconProps?.className || (active ? 'primary' : 'grey50')}
            name={iconProps?.name || 'info_outline'}
          />
        )}
      </span>
      <ReactTooltip
        // Props can override these
        delayHide={delay ?? 500}
        className="neos-tooltip"
        style={{
          backgroundColor: tooltipBackgroundColor ?? theme.colours.primaryB1,
          maxWidth: config.maxWidth || '280px',
          fontSize: '1rem',
          whiteSpace: 'pre-line',
        }}
        portalRoot={portalRoot}
        positionStrategy="fixed"
        clickable
        opacity={1}
        {...(resolvedOpenOnClick !== undefined ? { openOnClick: resolvedOpenOnClick } : {})}
        {...(resolvedOpenEvents ? { openEvents: resolvedOpenEvents } : {})}
        {...(closeEvents ? { closeEvents } : {})}
        {...tooltipProps}
        // These will override props
        id={id}
        content={content ? undefined : text}
        // Render tooltip content if provided
        {...(content ? { render: () => content } : {})}
        afterShow={() => {
          setActive(true);
          afterShow?.();
        }}
        afterHide={() => {
          setActive(false);
          afterHide?.();
        }}
      />
    </StyledTooltip>
  );
};
